// src/components/RelatedArticles.jsx
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../config';
import { Link as RouterLink } from 'react-router-dom';
import { Badge } from "../ui/badge";

// Safe date parser (ISO, Date, timestamp, Mongo Extended JSON)
const parseDate = (value) => {
  if (!value) return null;
  let raw = value;
  if (typeof value === 'object' && value && '$date' in value) {
    const v = value.$date;
    raw = (typeof v === 'object' && v && '$numberLong' in v) ? Number(v.$numberLong) : v;
  }
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const formatUTCDate = (value, locale = 'en-US') => {
  const d = parseDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
};

const RelatedArticles = ({ currentSlug }) => {
  const { t, i18n } = useTranslation();
  const [otherArticles, setOtherArticles] = useState([]);

  // en → no prefix, others use /{lang}
  const languagePrefix = i18n.language === 'en' ? '' : `/${i18n.language}`;

  useEffect(() => {
    const fetchOtherArticles = async () => {
      try {
        const res = await fetch(`${API_URL}/articles-cdn?excludeSlug=${encodeURIComponent(currentSlug)}&limit=4`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setOtherArticles(Array.isArray(data?.articles) ? data.articles : []);
      } catch (error) {
        console.error('Error fetching other articles:', error);
        setOtherArticles([]); // just in case
      }
    };
    fetchOtherArticles();
  }, [currentSlug]);

  if (!otherArticles.length) return null;

  return (
    <div className="py-12">
      <h2 className="text-2xl font-semibold mb-6">{t('other.t4')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
        {otherArticles.map(other => {
          const dateText = formatUTCDate(other.publish_date, 'en-US');
          return (
            <RouterLink
              key={other._id}
              to={`${languagePrefix}/blog/${other.url_slug}`}
              className="block border border-gray-300 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow bg-white"
            >
              {other.img_url && (
                <div className="w-full h-40 overflow-hidden">
                  <img
                    src={other.img_url}
                    alt={other.title}
                  />
                </div>
              )}
              <div className="relative flex flex-col flex-grow p-4 justify-between">
                <div>
                  <h2 className="text-lg font-semibold ">{other.title}</h2>
                  <p className="mt-4 text-sm text-stone-950">{other.short_description}</p>
                </div>
                <div className="mt-4">
                  {dateText && (
                    <Badge
                      text={dateText}
                      className="bg-stone-700 text-white shadow-lg"
                    />
                  )}
                </div>
              </div>
            </RouterLink>
          );
        })}
      </div>
    </div>
  );
};

export default RelatedArticles;
